import { toast } from "sonner";

// A plain utility called from event handlers, not a component or a hook, so
// there is no React context to read `t` from — resolve against the shared
// i18n singleton at call time instead.
import i18n from "@/lib/i18n";

/**
 * Copy a rendered AI message to the clipboard preserving formatting so
 * pastes into rich-text targets (Google Docs, Gmail, Notion, Word, etc.)
 * keep bold/italic/lists/links/headings/tables. Plain-text targets get the
 * original markdown so it stays readable.
 *
 * Strategy:
 *  - Read the rendered DOM (the prose container) for the message → text/html
 *  - Keep the original markdown source → text/plain
 *  - Use the async Clipboard API with ClipboardItem when available so both
 *    MIME types travel together. Fall back to writeText(markdown) otherwise.
 */
export async function copyRichText(opts: {
  /** Selector that uniquely identifies the rendered message container. */
  selector: string;
  /** Markdown source used for text/plain fallback. */
  plain: string;
  /** Toast message on success. */
  successMessage?: string;
}) {
  const { selector, plain, successMessage = i18n.t("mutations.copiedWithFormatting") } = opts;
  const node = typeof document !== "undefined"
    ? document.querySelector<HTMLElement>(selector)
    : null;
  const html = node
    ? `<div>${node.innerHTML}</div>`
    : `<pre>${plain.replace(/[&<>]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]!))}</pre>`;

  try {
    if (typeof ClipboardItem !== "undefined" && navigator.clipboard?.write) {
      const item = new ClipboardItem({
        "text/html": new Blob([html], { type: "text/html" }),
        "text/plain": new Blob([plain], { type: "text/plain" }),
      });
      await navigator.clipboard.write([item]);
      toast.success(successMessage);
      return;
    }
  } catch {
    // fall through to plain-text fallback
  }

  try {
    await navigator.clipboard.writeText(plain);
    toast.success(i18n.t("mutations.copiedToClipboard"));
  } catch {
    toast.error(i18n.t("mutations.copyFailed"));
  }
}